/**
 * IndexTestsPanel.tsx
 * Phase 2 — Index tests body (Item 5.2: per-type rows + window pickers).
 * Renumbered from Phase 3 to Phase 2 by Item 9.1 (R1 — owner revision
 * 2026-07-23: Machine → Index → Employee planning).
 *
 * Mounted by ScenarioPhaseStepper only while `phase === 2` (and the tab is
 * enabled — see indexTabAvailability.ts, I20).
 *
 * Renders one row per pooled IndexAllocation for this scenario+source (I17 —
 * one row per index TaskType), each with a window (Start/End) editor, the
 * computed working-day count, per-day fractional spread (I10), and %
 * contribution against the TaskType's IndexTestsPerDay (I4). TestCount is
 * read-only here — it's quote-derived and auto re-spread on qty edits (I19).
 *
 * Row math is presentation-only optimism: `indexRowMath.ts` mirrors the
 * backend `indexCapacity.ts` (the source of truth) so the per-day/% columns
 * update instantly on a date edit rather than waiting on the PATCH
 * round-trip. The day-by-day shared-machine utilization heatmap (I13,
 * `IndexCapacityHeatmap`) is mounted below the table (Item 5.3).
 *
 * A Submitted/Archived scenario is read-only (mirrors Phase1Panel/Phase2Panel).
 *
 * Item 5.4 — Compare-to-Baseline ghosting (I16): when `compareToBaseline` is
 * ON and the scenario is still editable (`isCompareToBaselineEligible`), a
 * second fetch pulls this source's BASELINE-scenario-keyed allocations
 * (`listIndexAllocations('BASELINE', source)` — no new backend work) and
 * diffs them against the draft rows (`indexBaselineDiff.ts`, pure/testable).
 * Per-row TestCount/window ghosts are rendered by `IndexAllocationRow`; the
 * day-cell heatmap ghost is built and rendered inside `IndexCapacityHeatmap`.
 * Bulk actions are explicitly out of scope for this item.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ScienceIcon from '@mui/icons-material/Science';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { useScheduleHolidays } from '../../../../../hooks/useScheduleHolidays';
import {
  listIndexAllocations,
  patchIndexAllocation,
  type IndexAllocation,
  type PatchIndexAllocationRequest,
} from '../../../../../services/scheduleIndex';
import type { Scenario } from '../../../../../services/scheduleScenario';
import { IndexAllocationRow } from './IndexAllocationRow';
import { IndexCapacityHeatmap } from './IndexCapacityHeatmap';
import { buildRowBaselineDiffMap, isCompareToBaselineEligible } from './indexBaselineDiff';

interface IndexTestsPanelProps {
  scenario: Scenario | null;
  /** 6D.2-style on-enter refresh key. */
  onEnterRefreshKey?: number;
  /** Item 5.4 — header-chrome Compare-to-Baseline toggle (I16). */
  compareToBaseline?: boolean;
}

/** Extract the backend's error message from an axios-style error (mirrors useLockedPlanChoice.ts). */
function errorMessage(err: unknown, fallback: string): string {
  const apiMsg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  if (apiMsg) return apiMsg;
  return err instanceof Error ? err.message : fallback;
}

const COLUMNS = [
  { id: 'type', label: 'Test type', align: 'left' as const },
  { id: 'count', label: 'Test count', align: 'center' as const, tooltip: 'Σ quote-line quantities pooled into this type (I3). Read-only — recomputed on quote qty edits (I19).' },
  { id: 'start', label: 'Start date', align: 'center' as const },
  { id: 'end', label: 'End date', align: 'center' as const },
  { id: 'workingDays', label: 'Working days', align: 'center' as const, tooltip: 'Working days (Mon–Fri, stat holidays excluded) in the window.' },
  { id: 'perDay', label: 'Per day', align: 'center' as const, tooltip: 'Test count spread evenly across the working days (I10 — fractional).' },
  { id: 'pct', label: '% of capacity', align: 'center' as const, tooltip: "This allocation's per-day count ÷ the type's IndexTestsPerDay capacity." },
];

export const IndexTestsPanel: React.FC<IndexTestsPanelProps> = ({
  scenario,
  onEnterRefreshKey = 0,
  compareToBaseline = false,
}) => {
  const theme = useTheme();

  const source: 'Quote' | 'WorkOrder' | null = scenario?.SourceType ?? null;
  const sourceKey: string | null =
    source === 'Quote' ? (scenario?.QuoteKey ?? null) : (scenario?.WorkOrderKey ?? null);
  const scenarioKey: string | null = scenario?.ScenarioKey ?? null;

  const [allocations, setAllocations] = useState<IndexAllocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAllocations = useCallback(async () => {
    if (!scenarioKey || !source || !sourceKey) {
      setAllocations([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const rows = await listIndexAllocations(scenarioKey, { sourceType: source, sourceKey });
      // 118: the Phase-2 Index panel is the tests/day capacity surface —
      // effort-kind allocations (Lab PM) live only in the People phase.
      setAllocations(rows.filter((r) => (r.Kind ?? 'index') === 'index'));
    } catch (err) {
      setError(errorMessage(err, 'Failed to load index tests.'));
    } finally {
      setLoading(false);
    }
  }, [scenarioKey, source, sourceKey]);

  useEffect(() => {
    void loadAllocations();
  }, [loadAllocations]);

  // ── Item 5.4 — Compare-to-Baseline row diff ────────────────────────────────
  // Only meaningful for a still-editable draft (a Submitted/Archived scenario
  // effectively IS the baseline, per isCompareToBaselineEligible).
  const baselineEligible = compareToBaseline && isCompareToBaselineEligible(scenario?.Status);

  const [baselineAllocations, setBaselineAllocations] = useState<IndexAllocation[]>([]);

  useEffect(() => {
    if (!baselineEligible || !source || !sourceKey) {
      setBaselineAllocations([]);
      return;
    }
    let cancelled = false;
    listIndexAllocations('BASELINE', { sourceType: source, sourceKey })
      .then((rows) => {
        if (!cancelled) setBaselineAllocations(rows);
      })
      .catch(() => {
        // Best-effort — ghost layer simply stays empty on error (mirrors Phase1MachineGrid).
        if (!cancelled) setBaselineAllocations([]);
      });
    return () => {
      cancelled = true;
    };
  }, [baselineEligible, source, sourceKey, onEnterRefreshKey]);

  const rowBaselineDiffMap = useMemo(
    () => buildRowBaselineDiffMap(allocations, baselineAllocations),
    [allocations, baselineAllocations],
  );

  // 6D.2 — on-enter refresh (skip value 0 = initial mount).
  const onEnterRefreshKeyRef = useRef(0);
  useEffect(() => {
    if (onEnterRefreshKey === 0) return;
    if (onEnterRefreshKey === onEnterRefreshKeyRef.current) return;
    onEnterRefreshKeyRef.current = onEnterRefreshKey;
    void loadAllocations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onEnterRefreshKey, loadAllocations]);

  // Stat-holiday window spanning every row's planning window, so the
  // working-day/spread math agrees with the server (I7). Falls back to
  // today when there are no rows yet.
  const holWindow = useMemo(() => {
    const starts = allocations.map((a) => a.StartDate).sort();
    const ends = allocations.map((a) => a.EndDate).sort();
    const today = new Date().toISOString().slice(0, 10);
    return { start: starts[0] ?? today, end: ends[ends.length - 1] ?? today };
  }, [allocations]);
  const { holidays: holidayRows } = useScheduleHolidays(holWindow.start, holWindow.end);
  const holidayDates = useMemo(() => holidayRows.map((h) => h.date.slice(0, 10)), [holidayRows]);

  // Optimistic window-edit: update local state immediately, persist via PATCH,
  // and revert (+ surface the server's message) on 400 (invalid window) or
  // 409 (scenario locked) — mirrors Phase1Panel's booking-start handlers.
  //
  // patchSerial (2026-07-28 stale-heatmap fix): the optimistic update re-fires
  // the heatmap's capacity fetch immediately, which RACES the PATCH on the
  // server and can return pre-commit percentages; once the PATCH resolves the
  // allocation content is identical to the optimistic state, so the heatmap's
  // fingerprint never changes again and the stale numbers stuck until a manual
  // page reload. Bumping the serial after the server confirms guarantees one
  // post-commit refetch.
  const [patchSerial, setPatchSerial] = useState(0);
  const handlePatchWindow = useCallback(
    async (key: string, data: PatchIndexAllocationRequest) => {
      const previous = allocations;
      setAllocations((rows) =>
        rows.map((r) => (r.IndexAllocationKey === key ? { ...r, ...data } : r)),
      );
      try {
        const updated = await patchIndexAllocation(key, data);
        setAllocations((rows) =>
          rows.map((r) => (r.IndexAllocationKey === key ? { ...r, ...updated } : r)),
        );
        setPatchSerial((n) => n + 1);
      } catch (err) {
        setAllocations(previous);
        setError(errorMessage(err, 'Failed to update the planning window.'));
      }
    },
    [allocations],
  );

  const locked = scenario?.Status === 'Submitted' || scenario?.Status === 'Archived';

  // ── No scenario guard ──────────────────────────────────────────────────────
  if (!scenario || !source || !sourceKey) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 5,
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <ScienceIcon sx={{ fontSize: '2.5rem', color: 'text.disabled' }} />
        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
          Index tests
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center">
          Select or create a scenario above to plan index test windows.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {locked && (
        <Alert
          severity="info"
          icon={<LockOutlinedIcon fontSize="inherit" />}
          sx={{ '& .MuiAlert-message': { fontSize: '0.82rem' } }}
        >
          This plan is <strong>{scenario.Status === 'Archived' ? 'archived' : 'scheduled'}</strong> —
          index test windows are read-only. Use <strong>Reopen to Edit</strong> (top of the page) to make changes.
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
        <ScienceIcon sx={{ fontSize: 18, color: theme.schedule.machineTime }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
          Index Tests (pooled, virtual Index machine)
        </Typography>
      </Box>

      <Divider />

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && allocations.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty state — the tab shouldn't normally be reachable with zero rows
          (I20 grey-out), but stays friendly for a stale/direct-nav edge case. */}
      {!loading && allocations.length === 0 && !error && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            gap: 1.5,
          }}
        >
          <ScienceIcon sx={{ fontSize: 48, opacity: 0.3, color: theme.schedule.machineTime }} />
          <Typography variant="body1" fontWeight={500} color="text.secondary">
            No index tests
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 340 }}>
            This source has no pooled index-test allocations yet. They're created automatically
            when the plan is generated from a quote/work order with index-flagged test lines.
          </Typography>
        </Box>
      )}

      {allocations.length > 0 && (
        <Box
          sx={{
            border: `1px solid ${theme.border.color}`,
            borderRadius: `${theme.border.radius}px`,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
            ...(locked ? { opacity: 0.6, pointerEvents: 'none' as const } : {}),
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sx={{
                      backgroundColor: theme.table.headerBg,
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      letterSpacing: 0.3,
                      py: 1,
                      px: 1.5,
                      borderBottom: `2px solid ${theme.border.color}`,
                    }}
                  >
                    {col.tooltip ? (
                      <Tooltip title={col.tooltip} placement="top">
                        <span>{col.label}</span>
                      </Tooltip>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {allocations.map((allocation) => (
                <IndexAllocationRow
                  key={allocation.IndexAllocationKey}
                  allocation={allocation}
                  holidays={holidayDates}
                  locked={locked}
                  onPatchWindow={handlePatchWindow}
                  baselineDiff={
                    baselineEligible ? (rowBaselineDiffMap.get(allocation.TaskTypeKey) ?? null) : null
                  }
                />
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Item 5.3 — day-by-day shared-machine utilization heatmap (I13).
          Item 5.4 — passes compareToBaseline through so the grid can overlay
          the BASELINE-perspective ghost per day cell. */}
      <IndexCapacityHeatmap
        scenarioKey={scenarioKey}
        allocations={allocations}
        onEnterRefreshKey={onEnterRefreshKey}
        dataRefreshKey={patchSerial}
        compareToBaseline={baselineEligible}
      />
    </Box>
  );
};
